import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AdminPageLayout from "../../../components/AdminPageLayout/AdminPageLayout";
import { Headings } from "../../../components/Headings";
import LoadingMask from "../../../components/LoadingMask/LoadingMask";
import { useAppAuthState } from "../../../hooks/useAppAuthState";
import { useUserProfile } from "../../../hooks/useUserProfile";
import {
  getUserById,
  resetUserOnboarding,
} from "../../../services/userService";
import { User } from "../../../types/User";
import { debugLogError } from "../../../utils/debug-log-error";
import "./AdminUserProfilePage.scss";

const AdminUserProfilePage: React.FC = () => {
  const { userId: targetUserId } = useParams<{ userId: string }>();
  const { isUserAdmin, isAuthLoading } = useAppAuthState();
  const [targetUser, setTargetUser] = useState<User | null>(null);
  const { profile: targetUserProfile } = useUserProfile(targetUser?.uid);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isResetting, setIsResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    if (!isUserAdmin) {
      setError("Access Denied: You must be an admin to view this page.");
      setIsLoading(false);
      return;
    }

    if (!targetUserId) {
      setError("User ID is missing from URL.");
      setIsLoading(false);
      return;
    }

    const fetchUser = async () => {
      setIsLoading(true);
      try {
        const fetchedUser = await getUserById(targetUserId);
        if (fetchedUser) {
          setTargetUser(fetchedUser);
        } else {
          setError("User not found.");
        }
      } catch (err: any) {
        debugLogError(`Error fetching user ${targetUserId}:`, err);
        setError(err.message || `Failed to fetch user ${targetUserId}.`);
      } finally {
        setIsLoading(false);
      }
    };

    if (isUserAdmin && targetUserId) {
      fetchUser();
    }
  }, [isUserAdmin, isAuthLoading, targetUserId]);

  const handleResetOnboarding = useCallback(async () => {
    if (!targetUser) {
      return;
    }

    setIsResetting(true);
    setResetError(null);
    try {
      await resetUserOnboarding(targetUser.uid);
      // Update local state to reflect the change immediately
      setTargetUser((prevUser) => {
        if (prevUser) {
          return { ...prevUser, onboardingCompleted: false };
        }
        return null;
      });
      // Optionally, display a success message or clear error after a delay
      // For now, successful update of state is the primary feedback
    } catch (err: any) {
      console.error("Error resetting onboarding status:", err);
      setResetError(err.message || "Failed to reset onboarding status.");
    } finally {
      setIsResetting(false);
    }
  }, [targetUser]);

  const pageContent = useMemo(() => {
    if (isLoading || isAuthLoading) {
      return null;
    }

    if (error) {
      return <div className="alert alert-danger">{error}</div>;
    }

    if (!isUserAdmin) {
      return (
        <div className="alert alert-danger">
          Access Denied. You must be an admin to view this page.
        </div>
      );
    }

    if (!targetUser) {
      // This case should ideally be covered by the error state if user not found
      return <p>User not found.</p>;
    }

    return (
      <div className="admin-user-profile-container">
        <Link to="/admin/users" className="btn btn-outline-primary">
          &larr; Admin: Users
        </Link>
        <Headings.Page>{targetUser.email}</Headings.Page>
        <div className="profile-details">
          <div className="profile-photo-section">
            {targetUserProfile?.photoURL ? (
              <img
                src={targetUserProfile.photoURL}
                alt={targetUserProfile.username || targetUser.email || "User"}
                className="profile-photo"
              />
            ) : (
              <div className="profile-photo-placeholder">
                <span>No Photo</span>
              </div>
            )}
          </div>
          <div className="profile-info-section">
            <p>
              <strong>UID:</strong> {targetUser.uid}
            </p>
            <p>
              <strong>Email:</strong> {targetUser.email || "N/A"}
            </p>
            <p>
              <strong>Handle:</strong> {targetUserProfile?.username || "N/A"}
            </p>
            <p>
              <strong>Created At:</strong>{" "}
              {targetUserProfile?.createdAt
                ? new Date(targetUserProfile.createdAt).toLocaleString()
                : "N/A"}
            </p>
            <p>
              <strong>Onboarding Completed:</strong>{" "}
              {targetUser.onboardingCompleted ? "Yes" : "No"}
            </p>
            <div className="admin-actions-section">
              <button
                onClick={handleResetOnboarding}
                disabled={isResetting || !targetUser}
                className="btn btn-warning"
              >
                {isResetting ? "Resetting..." : "Reset Onboarding Status"}
              </button>
              {resetError && (
                <div className="alert alert-danger mt-2">{resetError}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }, [
    isLoading,
    isAuthLoading,
    error,
    isUserAdmin,
    targetUser,
    targetUserProfile,
    handleResetOnboarding,
    isResetting,
    resetError,
  ]);

  if (isLoading || isAuthLoading) {
    return <LoadingMask message="Loading user profile..." />;
  }

  return (
    <AdminPageLayout title={`User: ${targetUser?.email}`}>
      {pageContent}
    </AdminPageLayout>
  );
};

export default AdminUserProfilePage;
